import React, { Component } from 'react';
import ReactPlayer from 'react-player';
import AgeGateManager from '../utils/ageGate';
import { getSocketApiBase } from '../utils/apiBase';

const parseClockDuration = (value) => {
    if (!value || typeof value !== 'string' || !value.includes(':')) return 0;
    const [minutes, seconds] = value.split(':').map((part) => Number(part));
    if (Number.isNaN(minutes) || Number.isNaN(seconds)) return 0;
    return (minutes * 60) + seconds;
};

const formatCompactViews = (count) => {
    if (!count) return null;
    if (count < 1000) return `${count} views`;
    if (count < 1000000) return `${Math.round(count / 1000)}K views`;
    return `${(count / 1000000).toFixed(1)}M views`;
};

const formatDurationLabel = (duration) => {
    if (!duration) return null;
    if (typeof duration === 'string' && duration.includes(':')) return duration;
    if (typeof duration === 'number' && Number.isFinite(duration)) {
        const total = Math.floor(duration);
        const mm = Math.floor(total / 60);
        const ss = (total % 60).toString().padStart(2, '0');
        return `${mm}:${ss}`;
    }
    return String(duration);
};

class MainVideoPlayer extends Component {
    constructor(props) {
        super(props);
        const reviewDurationSeconds = parseClockDuration(props.video?.duration);
        const reviewPlayedSeconds = props.video?.reviewPoster ? (8 * 60) + 36 : 0;
        this.state = {
            playing: false,
            played: reviewDurationSeconds ? reviewPlayedSeconds / reviewDurationSeconds : 0,
            duration: reviewDurationSeconds,
            playedSeconds: reviewPlayedSeconds,
            volume: 0.8,
            muted: false,
            showControls: true,
            playerError: '',
            retryKey: 0
        };
    }

    handlePlayPause = () => {
        this.setState({ playing: !this.state.playing });
    };

    handleProgress = (state) => {
        this.setState({ 
            played: state.played,
            playedSeconds: state.playedSeconds 
        });
    };

    handleDuration = (duration) => {
        this.setState({ duration });
    };

    handleSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const progress = clickX / rect.width;
        this.setState({ played: progress });
        if (this.player) {
            this.player.seekTo(progress);
        }
    };

    handleSeekKeyDown = (event) => {
        if (!this.player || !this.state.duration) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextSeconds = Math.min(
            this.state.duration,
            Math.max(0, this.state.playedSeconds + (direction * 5))
        );
        this.player.seekTo(nextSeconds, 'seconds');
        this.setState({
            playedSeconds: nextSeconds,
            played: nextSeconds / this.state.duration
        });
    };

    handleVolumeChange = (e) => {
        this.setState({ volume: parseFloat(e.target.value) });
    };

    toggleMute = () => {
        this.setState({ muted: !this.state.muted });
    };

    handleFullscreen = () => {
        if (this.playerWrapper && this.playerWrapper.requestFullscreen) {
            this.playerWrapper.requestFullscreen();
        }
    };

    retryPlayback = () => {
        this.setState((state) => ({
            playerError: '',
            retryKey: state.retryKey + 1,
            playing: true
        }));
    };

    formatTime = (seconds) => {
        if (!seconds || Number.isNaN(seconds)) return '0:00';
        const total = Math.floor(seconds);
        const mm = Math.floor(total / 60).toString().padStart(2, '0');
        const ss = (total % 60).toString().padStart(2, '0');
        return `${mm}:${ss}`;
    };

    renderReviewControls = () => {
        const { duration, playedSeconds } = this.state;
        const progress = duration ? playedSeconds / duration : 0;

        return (
            <div className="player-controls review-player-controls">
                <div className="progress-bar" aria-hidden="true">
                    <div className="progress-played" style={{ width: `${progress * 100}%` }} />
                    <div className="progress-handle" style={{ left: `${progress * 100}%` }} />
                </div>
                <div className="controls-row">
                    <div className="controls-left">
                        <span className="control-btn play-pause-btn" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z"/>
                            </svg>
                        </span>
                        <span className="control-btn" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z"/>
                            </svg>
                        </span>
                        <span className="control-btn volume-btn" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                            </svg>
                        </span>
                        <span className="time-display">
                            {this.formatTime(playedSeconds)} / {this.formatTime(duration)}
                        </span>
                    </div>
                    <div className="controls-right">
                        <span className="control-btn" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.12.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.04.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.12-.57 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.03-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                            </svg>
                        </span>
                        <span className="control-btn" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z"/>
                            </svg>
                        </span>
                        <span className="control-btn fullscreen-btn" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                            </svg>
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { video, catalogHasResults } = this.props;
        const { playing, played, duration, playedSeconds, volume, muted, showControls, playerError, retryKey } = this.state;

        if (!video) {
            const waitingForPick = Boolean(catalogHasResults);
            const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
            return (
                <div className="video-player-container">
                    <div className="video-player-placeholder">
                        <div className="placeholder-content">
                            <div className="placeholder-icon" aria-hidden="true">
                                <svg width="42" height="42" viewBox="0 0 48 48" fill="none">
                                    <rect x="6" y="10" width="36" height="28" rx="3" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M20 18l12 6-12 6V18z" fill="currentColor"/>
                                </svg>
                            </div>
                            <div className="placeholder-title">
                                {waitingForPick ? 'Choose a result' : 'Search to begin'}
                            </div>
                            <div className="placeholder-subtitle">
                                {waitingForPick
                                    ? (isMobile
                                        ? 'Tap a row in the results list to start playback here.'
                                        : 'Select a row in the catalog on the left to load it in the player.')
                                    : 'Enter a title, keyword, or paste a link above. Results appear in the catalog beside the player.'}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (video.resolving || video.resolveError) {
            return (
                <div className="video-player-container">
                    <div className="video-player-placeholder" role={video.resolveError ? 'alert' : 'status'}>
                        <div className="placeholder-content">
                            <div className="placeholder-icon" aria-hidden="true">
                                <svg width="42" height="42" viewBox="0 0 48 48" fill="none">
                                    <rect x="6" y="10" width="36" height="28" rx="3" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M20 18l12 6-12 6V18z" fill="currentColor"/>
                                </svg>
                            </div>
                            <div className="placeholder-title">
                                {video.resolving ? 'Preparing video…' : 'Video unavailable'}
                            </div>
                            <div className="placeholder-subtitle">
                                {video.resolving
                                    ? 'Resolving a fresh playback stream from the source.'
                                    : video.resolveError}
                            </div>
                        </div>
                    </div>
                    {video.title && (
                        <div className="video-info">
                            <h3 className="video-title">{video.title}</h3>
                        </div>
                    )}
                </div>
            );
        }

        const isReviewPoster = Boolean(video.reviewPoster);
        const metaParts = [
            video.uploader || 'Unknown',
            formatDurationLabel(video.duration),
            formatCompactViews(video.view_count),
            video.upload_date
        ].filter(Boolean);
        const metaLine = metaParts.join(' • ');

        // Check if this is a direct video URL that needs proxying
        const isDirectVideoUrl = video.playable === true || (video.url && (
            video.url.includes('.mp4') || 
            video.url.includes('.m3u8') || 
            video.url.includes('.webm') ||
            video.url.includes('.mov')
        ));
        
        const isWebpageUrl = video.url && (
            video.url.includes('/view_video.php') ||
            video.url.includes('/watch?v=') ||
            video.url.includes('/videos/')
        );
        
        // Check if this is an external URL that needs proxying for CORS
        const isExternalUrl = video.url && (
            video.url.includes('pornhub.com') || 
            video.url.includes('redtube.com') ||
            video.url.includes('phncdn.com') ||
            video.url.includes('ev.phncdn.com') ||
            video.url.includes('xnxx.com') ||
            video.url.includes('xnxx-cdn.com') ||
            video.url.includes('xvideos.com') ||
            video.url.includes('xvideos-cdn.com')
        );
        
        console.log('🎬 Video URL analysis:', {
            originalUrl: video.url,
            isDirectVideoUrl,
            isWebpageUrl,
            isExternalUrl
        });
        
        // Proxy external video URLs to avoid CORS issues
        const backendHost = getSocketApiBase();
        const sourceUrlParam = video.source_url
            ? `&sourceUrl=${encodeURIComponent(video.source_url)}`
            : '';
        const providerParam = video.source
            ? `&provider=${encodeURIComponent(video.source)}`
            : '';
        const sourceIdParam = video.source_id
            ? `&sourceId=${encodeURIComponent(video.source_id)}`
            : '';
        const videoUrl = isDirectVideoUrl && isExternalUrl
            ? `${backendHost}/proxy-video?url=${encodeURIComponent(video.url)}&ageGateToken=${encodeURIComponent(AgeGateManager.getAgeGateToken() || '')}${sourceUrlParam}${providerParam}${sourceIdParam}`
            : video.url;
            
        // Check if ReactPlayer can handle this URL
        const isReactPlayerSupported = isDirectVideoUrl || 
            video.url?.includes('youtube.com') || 
            video.url?.includes('vimeo.com') ||
            video.url?.includes('soundcloud.com');
            
        console.log('🎬 Player decision:', {
            originalVideoUrl: video.url,
            processedVideoUrl: videoUrl,
            isReactPlayerSupported,
            willUseIframe: !isReactPlayerSupported,
            isDirectVideoUrl,
            isExternalUrl
        });

        return (
            <div className="video-player-container">
                <div className="main-video-player">
                    <div className="player-wrapper" ref={(element) => { this.playerWrapper = element; }}>
                        {isReviewPoster ? (
                            <img className="review-player-poster" src={video.reviewPoster} alt="" />
                        ) : isReactPlayerSupported ? (
                            <ReactPlayer
                                key={retryKey}
                                ref={player => { this.player = player; }}
                                className="react-player"
                                url={videoUrl}
                                width="100%"
                                height="100%"
                                playing={playing}
                                volume={volume}
                                muted={muted}
                                onProgress={this.handleProgress}
                                onDuration={this.handleDuration}
                                onEnded={() => this.setState({ playing: false })}
                                onError={(error) => {
                                    console.error('🚨 ReactPlayer Error:', error);
                                    this.setState({
                                        playing: false,
                                        playerError: 'This source could not be played. Try again or choose another result.'
                                    });
                                }}
                                onReady={() => {
                                    this.setState({ playerError: '' });
                                }}
                                config={{
                                    file: {
                                        attributes: {
                                            crossOrigin: 'anonymous'
                                        },
                                        forceVideo: !videoUrl.includes('.m3u8'),
                                        hlsOptions: {
                                            enableWorker: false
                                        }
                                    }
                                }}
                            />
                        ) : (
                            <div className="iframe-player-container">
                                <div className="external-player-message">
                                    <div className="external-player-title">
                                        External video
                                    </div>
                                    <div className="external-player-copy">
                                        This video is hosted on an external site
                                    </div>
                                    <a 
                                        href={videoUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="external-player-link"
                                        onClick={() => console.log('🔗 Opening external video:', videoUrl)}
                                    >
                                        Open source
                                    </a>
                                </div>
                            </div>
                        )}

                        {playerError && (
                            <div className="player-error" role="alert">
                                <strong>Playback issue</strong>
                                <span>{playerError}</span>
                                <button type="button" onClick={this.retryPlayback}>Retry</button>
                            </div>
                        )}
                        
                        {/* Play button overlay */}
                        {!playing && !playerError && isReactPlayerSupported && !isReviewPoster && (
                            <button
                                type="button"
                                className="player-overlay"
                                onClick={this.handlePlayPause}
                                aria-label="Play video"
                            >
                                <span className="play-button-large">
                                    <svg width="42" height="42" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                        <path d="M8 5v14l11-7z"/>
                                    </svg>
                                </span>
                            </button>
                        )}

                        {isReviewPoster ? this.renderReviewControls() : (
                        <div className="player-controls" style={{ opacity: showControls ? 1 : 0 }}>
                            <div
                                className="progress-bar"
                                onClick={this.handleSeek}
                                onKeyDown={this.handleSeekKeyDown}
                                role="slider"
                                tabIndex="0"
                                aria-label="Video progress"
                                aria-valuemin="0"
                                aria-valuemax={Math.round(duration || 0)}
                                aria-valuenow={Math.round(playedSeconds || 0)}
                            >
                                <div className="progress-buffer"></div>
                                <div 
                                    className="progress-played" 
                                    style={{ width: `${played * 100}%` }}
                                ></div>
                                <div 
                                    className="progress-handle" 
                                    style={{ left: `${played * 100}%` }}
                                ></div>
                            </div>
                            
                            <div className="controls-row">
                                <div className="controls-left">
                                    <button
                                        type="button"
                                        className="control-btn play-pause-btn"
                                        onClick={this.handlePlayPause}
                                        aria-label={playing ? 'Pause video' : 'Play video'}
                                    >
                                        {playing ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                                            </svg>
                                        ) : (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M8 5v14l11-7z"/>
                                            </svg>
                                        )}
                                    </button>
                                    
                                    <div className="volume-control">
                                        <button
                                            type="button"
                                            className="control-btn volume-btn"
                                            onClick={this.toggleMute}
                                            aria-label={muted ? 'Unmute video' : 'Mute video'}
                                        >
                                            {muted || volume === 0 ? (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                                                </svg>
                                            ) : (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                                                </svg>
                                            )}
                                        </button>
                                        <input
                                            type="range"
                                            min={0}
                                            max={1}
                                            step={0.1}
                                            value={muted ? 0 : volume}
                                            onChange={this.handleVolumeChange}
                                            className="volume-slider"
                                            aria-label="Volume"
                                        />
                                    </div>
                                    
                                    <span className="time-display">
                                        {this.formatTime(playedSeconds)} / {this.formatTime(duration)}
                                    </span>
                                </div>
                                
                                <div className="controls-right">
                                    <button
                                        type="button"
                                        className="control-btn fullscreen-btn"
                                        onClick={this.handleFullscreen}
                                        aria-label="Enter fullscreen"
                                    >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                    
                    <div className="video-info">
                        <h3 className="video-title">{video.title}</h3>
                        <p className="video-meta-line">{metaLine}</p>
                    </div>
                </div>
            </div>
        );
    }
}

export default MainVideoPlayer;